import {withRouter, useLocation} from "react-router-dom";
import React, {useState, useEffect, useCallback} from 'react';
import {useTranslation} from "react-i18next";
import {InstallationsContext} from "./App";
import "../css/printable.css";
import {SITE_LINKS} from "../Contants";
import CaseTableRow from "./CaseTableRow";
import {
  convertToDMS,
  extractFileName,
  timestampToLocalDateString,
  timestampToLocalString,
  timestampToLocalTimeString
} from "../utilities";
import JournalEntry from "./JournalEntry";
import {CASE_DESCRIPTION_TYPE_PROG_CALL, JOURNAL_PROG_CALL_RESPONSE_HAS_REPLIED} from "../api/wimhome-api";
import ResourceLink from "./ResourceLink";
import {
  faMapMarkedAlt
} from '@fortawesome/free-solid-svg-icons';
import {Puff} from "react-loader-spinner";

export default withRouter(function PrintablePage(props) {
  const JOURNAL_PROG_CALL_BG_COLOR = '#33ccff'

  const LOG_FILTER_EVENTS = 0;
  const LOG_FILTER_DATA = 1;
  const LOG_FILTER_EVENTS_AND_DATA = 2;

  const {history} = props;
  const {t, i18n} = useTranslation();
  const {
    loadInstallationCases,
    loadInstallationDetails,
    loadInstJournal,
    loadAllInstallationData,
    requestInstallationEvents,
    requestInstallationData,
  } = React.useContext(InstallationsContext);

  function useQuery() {
    const { search } = useLocation();
    return new URLSearchParams(search);
  }

  const q = useQuery();

  const [requestParams, setRequestedParams] = useState({
    inst: q.get("inst"),
    type: q.get("type"),
    page: q.get("page"),
    pageSize: q.get("pageSize"),
    showClosed: q.get("showClosed") ?? true,
    orderByLastCaseUpdate: q.get("orderByLastCaseUpdate") === "true",
    latestFirst: q.get("latestFirst") === "true",
    startDateTime: q.get("startDateTime"),
    endDateTime: q.get("endDateTime"),
    onlyProgCalls: (q.get("onlyProgCalls") === "true"),
    showTechData: q.get("showTechData") === "true",
    logFilterType: isNaN(parseInt(q.get("logFilterType"))) ? LOG_FILTER_EVENTS : parseInt(q.get("logFilterType")),
    deviceList: q.getAll("deviceList[]") ?? null,
  });
  const [cases, setCases] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [logs, setLogs] = useState([]);
  const [ready, setReady] = useState(false);
  const [instDetails, setInstDetails] = useState(null);
  const [deviceInfos, setDeviceInfos] = useState(new Map());
  const [loadingInfo, setLoadingInfo] = React.useState(true);

  function renderLoadingSpinner() {
    return (
        <div
            className="embedded-loading-overlay-page"
            id="embedded-loading-overlay-page"
            style={loadingInfo ? null : {display: 'none'}}
        >
          <Puff
              color="#00BFFF"
              height={100}
              // visible={cannotChangeParams === true}
              visible={true}
              width={100}
              wrapperClass="loading-spinner"
              // timeout={3000} //3 secs
          />
        </div>
    );
  }

  function decodeValueCase(valueCase) {
    switch(valueCase) {
      case 0:
        return "normal-state";

      case 1:
        return "active-state";

      case 2:
        return "warning-state";

      case 3:
        return "error-state";

      default:
        return "";
    }
  }

  function jsonEventDescription(log) {
    const regexPosition = new RegExp('%position:([+-]*\\d+\\.\\d+),([+-]*\\d+\\.\\d+);([A-Za-zÀ-ÖØ-öø-ÿ\\d \'-]+|\\?)%', 'g');
    const matchesPosition = log.eventDesc.matchAll(regexPosition);
    let message = log.eventDesc;
    let positions = [];
    for(const match of matchesPosition) {
      //message = message.replace(match[0], `${t('latitude')}: ${match[1]}, ${t('longitude')}: ${match[2]}`)
      const coords = convertToDMS(parseFloat(match[1]), parseFloat(match[2]));
      if(match[3] !== '?')
        message = message.replace(match[0], `${match[3]} (${coords.fullString})`)
      else
        message = message.replace(match[0], coords.fullString)
      positions.push({"latitude": match[1], "longitude": match[2], "beaconLabel": match[3]})
//      return <a href={`https://www.google.com/maps/search/?api=1&query=${match[1]}%2C${match[2]}`} rel="noopener" target="_blank">{t('position')}</a>;
    }
    const regexTime = new RegExp('\\s*%time%\\s*,?', 'g');
    const matchesTime = log.eventDesc.matchAll(regexTime);
    for(const match of matchesTime) {
      message = message.replace(match[0], '')
    }
    return (
        <>
          <span>{log.name + ": " + message}</span> {positions.map((position, idx) => {
          return (<ResourceLink
              key={`${idx},${position.latitude},${position.longitude}`}
              className="installation-desc-link"
              icon={faMapMarkedAlt}
              linkState={{
                url: `https://www.google.com/maps/search/?api=1&query=${position.latitude}%2C${position.longitude}`,
                linkName: '',
                description: `${t('position')}: ${position.latitude},${position.longitude}`
              }}
          />)
        })}
        </>
    );
  }

  function intEventDescription(log) {
    let message = log.eventDesc;
    const regexTime = new RegExp('\\s*%time%\\s*', 'g');
    const matchesTime = log.eventDesc.matchAll(regexTime);
    for(const match of matchesTime) {
      message = message.replace(match[0], '')
    }
    return message;
  }

  const loadCases = useCallback((selectedInst, showClosedCases, latestCasesFirst, orderByLastCaseUpdate, offsetCase, limitCase) => {
    return loadInstallationCases(
        selectedInst,
        showClosedCases,
        latestCasesFirst,
        orderByLastCaseUpdate,
        offsetCase,
        limitCase,
        true,
        null
    )
        .then((loadedCases) => {
          setCases(loadedCases);
        })
        .catch((error) => {
          console.log(error);
          alert(error);
        })
  }, [setCases, loadInstallationCases]);

  const loadJournal = useCallback((selectedInst, latestFirst, offset, limit, startDateTime, endDateTime, onlyProgCalls) => {
    return loadInstJournal(selectedInst, latestFirst, offset, limit, startDateTime, endDateTime, onlyProgCalls)
        .then((retrievedJournal) => {
          setJournalEntries(retrievedJournal);
        })
        .catch((error) => {
          console.log(error);
          alert(error);
        })
  }, [setJournalEntries, loadInstJournal]);

  const loadData = useCallback((selectedInst, logFilterType, devToList, latestFirst, startDateTime, endDateTime, offset, limit, showTechData) =>
  {
    return ((logFilterType === LOG_FILTER_EVENTS) ? requestInstallationEvents(
        selectedInst,
        devToList,
        latestFirst,
        startDateTime,
        endDateTime,
        offset,
        limit
    ) : requestInstallationData(
        selectedInst,
        devToList,
        (logFilterType === LOG_FILTER_EVENTS_AND_DATA),
        showTechData,
        latestFirst,
        startDateTime,
        endDateTime,
        offset,
        limit,
        true
    ))
        .then((loadedData) => {
          setLogs(loadedData);
        })
        .catch((error) => {
          console.log(error);
        });
  }, [requestInstallationEvents, requestInstallationData, setLogs]);

  const loadInst = useCallback((selectedInst) => {
    return loadInstallationDetails(selectedInst)
    .then((instDetails) => {
      setInstDetails(instDetails);
    })
    .catch((error) => {
      console.log(error);
    })
  }, [setInstDetails, loadInstallationDetails]);

  useEffect(() => {
    if(!ready)
      return;

    if (requestParams.inst == null) {
      return;
    }

    setLoadingInfo(true);
    loadAllInstallationData(requestParams.inst, true, true, false).then((data) => {
      setDeviceInfos(data);
      setLoadingInfo(false);
    });
  }, [ready, loadAllInstallationData, requestParams.inst]);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if(!ready)
      return;

    if(requestParams.inst == null) {
      history.push(SITE_LINKS.INSTALLATIONS);
      return null;
    }

    if(requestParams.type !== "page" && requestParams.type !== "all") {
      history.push(SITE_LINKS.INSTALLATIONS);
      return null;
    }

    if(requestParams.type === "page" && (requestParams.page == null || requestParams.pageSize == null )) {
      history.push(SITE_LINKS.INSTALLATIONS);
      return null;
    }

    loadInst(requestParams.inst);

    switch(history.location.pathname) {
      case SITE_LINKS.PRINT_REQUESTS:
        if(requestParams.type === "all") {
          loadCases(requestParams.inst, requestParams.showClosed, requestParams.latestFirst,
              requestParams.orderByLastCaseUpdate, null, null)
        } else {
          loadCases(requestParams.inst, requestParams.showClosed, requestParams.latestFirst,
              requestParams.orderByLastCaseUpdate, requestParams.page, requestParams.pageSize)
        }
        break;

      case SITE_LINKS.PRINT_JOURNAL:
        if(requestParams.type === "all") {
          loadJournal(requestParams.inst, requestParams.latestFirst, null, null, null,
              null, requestParams.onlyProgCalls)
        } else {
          loadJournal(requestParams.inst, requestParams.latestFirst, requestParams.page, requestParams.pageSize,
              requestParams.startDateTime, requestParams.endDateTime, requestParams.onlyProgCalls)
        }
        break;

      case SITE_LINKS.PRINT_LOGS:
        if(requestParams.type === "all") {
          loadData(requestParams.inst, requestParams.logFilterType, requestParams.deviceList, requestParams.latestFirst,
              requestParams.startDateTime, requestParams.endDateTime, null, null, requestParams.showTechData)
        } else {
          loadData(requestParams.inst, requestParams.logFilterType, requestParams.deviceList, requestParams.latestFirst,
              requestParams.startDateTime, requestParams.endDateTime, requestParams.page, requestParams.pageSize,
              requestParams.showTechData)
        }
        break;

      default:
        history.push(SITE_LINKS.INSTALLATIONS);
        return null;
    }

  }, [ready, requestParams, history])

  function renderAlertsTableRows(instSerial, installationCases) {
    if(installationCases == null)
      return;

    return installationCases.map((caseInfo) => {
      return (
          <CaseTableRow
              key={caseInfo?.id}
              caseInfo={caseInfo}
              deviceInfo={
                  deviceInfos?.has(caseInfo?.deviceId)
                    ? deviceInfos.get(caseInfo?.deviceId)
                    : null
              }
              deviceInfos={null}
              inhibitHandler={null}
              installationOffline={false}
              openJournalHandler={null}
              reopenHandler={null}
              takeHandler={null}
          />
      );
    });
  }

  function renderDataTable(instSerial, data)
  {
    if(data == null)
      return;

    return (
        <table className="log-table">
          <thead>
          <tr>
            <th className="log-header-item log-header-date">{`${t('logPage.date')}`}</th>
            <th className="log-header-item log-header-time">{`${t('logPage.time')}`}</th>
            <th className="log-header-item log-header-icon"></th>
            <th className="log-header-item log-header-event">{`${t('logPage.registration')}`}</th>
          </tr>
          </thead>
          <tbody>
          {data?.map((log) => {
            return (
                <tr key={log.entryId}>
                  <td className="log-item log-row-date">{timestampToLocalDateString(log.timestamp)}</td>
                  <td className="log-item log-row-time">{timestampToLocalTimeString(log.timestamp)}</td>
                  <td className="log-item log-row-icon">
                      <span className={`icona ${decodeValueCase(log.valueCase)} table-icon`}>
                        {(!log.eventDesc) && (
                            <i className={`icon ${extractFileName(log.iconURL)}`}></i>
                        )}
                        {(log.eventDesc) && (
                            <i className={`icon ${extractFileName(log.iconURL)}`}></i>
                        )}
                      </span>
                  </td>
                  <td className="log-item log-row-event">
                    {((log.entryType === 1) || (log.entryType == null)) && (
                        log.eventDesc
                    )}
                    {((log.entryType === 0) && (log.valueType !== 'json')) && (
                        log.name + ": " + intEventDescription(log)
                    )}
                    {((log.entryType === 0) && (log.valueType === 'json')) && (
                        jsonEventDescription(log)
                    )}
                  </td>
                </tr>
            );
          })}
          </tbody>
        </table>
    );
  }

  function renderJournalTableRows(instSerial, journalEntries)
  {
    if(journalEntries == null)
      return;

    return (
        <div>
        {
          Array.from(journalEntries.values()).map((entry) => {
            return <JournalEntry
                key={entry.id}
                authorColor={entry.type === CASE_DESCRIPTION_TYPE_PROG_CALL ? JOURNAL_PROG_CALL_BG_COLOR : entry.userColor}
                authorIcon={entry.userIconUrl}
                authorName={entry.userFirstName}
                authorOrg={entry.userOrganization}
                authorSurname={entry.userLastName}
                caseDesc={entry.description ? entry.description : null}
                dateTime={entry.dateTimeUtc}
                deviceInfo={
                  deviceInfos?.has(entry?.entryDeviceId)
                      ? deviceInfos.get(entry?.entryDeviceId)
                      : null
                }
                entry={entry.entryDescription ? entry.entryDescription : (
                    entry?.caseData?.result ? (
                        entry?.caseData?.result === JOURNAL_PROG_CALL_RESPONSE_HAS_REPLIED ? t('journal.hasReplied') : t('journal.hasNotReplied')
                    ) : null
                )}
                nextCall={entry?.caseData?.nextCall}
                openDetails={true}
                progCallHasReply={entry?.caseData?.result}
                title={entry.title}
            />
          })
        }
        </div>
    );
  }

  if(!ready) {
    return <>{`${t('statusPage.loadingMessage')}`}</>;
  }

  return (
      <div className="print-container">
        {renderLoadingSpinner()}
        {/* Header/azioni visibili solo a schermo */}
        <div className="no-print">
          <button className={'cbutton back-to-status-bt'} onClick={() => window.print()}>{`${t('PRINT')}`}</button>
          <button className={'cbutton back-to-status-bt'} onClick={() => window.close()}>{`${t('CLOSE')}`}</button>
        </div>

        <h1>
          {instDetails?.title}
        </h1>
        <h2>
          {instDetails?.description}
        </h2>

        {(history.location.pathname === SITE_LINKS.PRINT_REQUESTS) && (requestParams.type === "all" || requestParams.type === "page") && (
          <>
            <h3>
              <center>
                {t("alertPageDetail.alertsDetailTitle")}
                {requestParams.type !== "all" && (
                    ` - ${t("excerpt")}`
                )}
              </center>
            </h3>
            <h5>
              <center>{`${t("updated")} ${(new Date()).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "medium" })}`}</center>
            </h5>
            {renderAlertsTableRows(requestParams.inst, cases)}
          </>
        )}

        {(history.location.pathname === SITE_LINKS.PRINT_JOURNAL) && (requestParams.type === "all" || requestParams.type === "page") && (
          <>
            <h3>
              <center>
                {`${t('journal.journalTitle')}`}
                {requestParams.type !== "all" && (
                    ` - ${t("excerpt")}`
                )}
              </center>
            </h3>
            <h5>
              <center>{`${t("updated")} ${(new Date()).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "medium" })}`}</center>
            </h5>
            {renderJournalTableRows(requestParams.inst, journalEntries)}
          </>
        )}

        {(history.location.pathname === SITE_LINKS.PRINT_LOGS) && (
          <>
            <h3>
              <center>
                {`${t('logPage.title')}`}
              </center>
            </h3>
            <h5>
              <center>{`${t('logPage.dateFrom')} ${timestampToLocalString(requestParams.startDateTime, i18n.language)} ${t('logPage.dateTo')} ${timestampToLocalString(requestParams.endDateTime, i18n.language)}`}
                {requestParams.type !== "all" && (
                    ` - ${t("excerpt")}`
                )}
              </center>
            </h5>
            {renderDataTable(requestParams.inst, logs)}
          </>
        )}
      </div>
  );
})